import { useState } from "react";
import { toast } from "sonner";
import { Send, Mail, MapPin } from "lucide-react";
import api from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and message required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Transmission received. We'll roll back soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Signal lost. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-black min-h-screen anim-fadeup" data-testid="contact-page">
      <section className="border-b border-neutral-900 py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-4">05 / CONTACT</div>
          <h1 className="font-bodoni font-black text-6xl md:text-8xl lg:text-9xl leading-[0.85]">
            Open <span className="italic">a channel</span>.
          </h1>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-12">
          <div className="col-span-12 md:col-span-4 space-y-10">
            <div>
              <div className="font-mono text-[10px] text-neutral-600 tracking-widest mb-3">DIRECT</div>
              <a href="mailto:hello@jitsuent.com" className="font-bodoni text-2xl link-underline">hello@jitsuent.com</a>
            </div>
            <div>
              <div className="font-mono text-[10px] text-neutral-600 tracking-widest mb-3">FAMILY</div>
              <a href="https://jointsnjitsu.com" target="_blank" rel="noreferrer" className="font-bodoni text-2xl link-underline">jointsnjitsu.com</a>
            </div>
            <div>
              <div className="font-mono text-[10px] text-neutral-600 tracking-widest mb-3">LOCATION</div>
              <div className="font-bodoni text-2xl flex items-center gap-2"><MapPin size={18} className="text-neutral-500" /> On the mat.</div>
            </div>
            <div className="border-t border-neutral-900 pt-6 font-mono text-[10px] text-neutral-700 tracking-widest leading-relaxed">
              FOR BOOKINGS, PRESS, COLLABORATIONS — TRANSMIT.
            </div>
          </div>

          <div className="col-span-12 md:col-span-8">
            {sent ? (
              <div className="border border-white p-12 text-center" data-testid="contact-success">
                <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-3">TRANSMISSION RECEIVED</div>
                <h3 className="font-bodoni text-4xl mb-3">Signal locked.</h3>
                <p className="font-mono text-sm text-neutral-400">We'll roll back within 48 hours.</p>
                <button onClick={() => setSent(false)} className="mt-6 btn-ghost">Send Another →</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Name" testid="contact-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                  <Field label="Email" testid="contact-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                </div>
                <Field label="Subject" testid="contact-subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
                <Field label="Message" testid="contact-message" textarea value={form.message} onChange={(v) => setForm({ ...form, message: v })} />
                <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-noir">
                  {busy ? "TRANSMITTING..." : <>Send Transmission <Send size={14} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, testid }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-2">{label.toUpperCase()}</div>
      {textarea ? (
        <textarea
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={6}
          className="noir-input"
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="noir-input"
        />
      )}
    </label>
  );
}
